'use client';
import cn from 'classnames';
import { useState, ChangeEvent } from 'react';
import { updateCartItem } from '@/util/cart_generic_client';
import LoadingSpinner from '@/components/common/LoadingSpinner/LoadingSpinner';

import Image from 'next/image';

import Button from '@/components/common/Button/Button';
import Link from '@/components/common/Link/Link';
import Select from '@/components/common/Select/Select';
import Text from '@/components/common/Text/Text';
import { useUser } from '@/components/hook/user';
import { useContextBroker } from '@/components/context/ContextBroker/useContextBroker';

import CloseIcon from 'public/img/icon/close.svg';
import FallbackImage from 'public/img/fallback-image.jpg';

import './EllaCartProduct.style.scss';
import { USDFormatter } from '@/util/currency';
import { CartItem } from '@/types/Cart';
import { ProductCardData } from '@/types/Product';

import analytics from '@/util/analytics';
import { CLOSET_SLOT_TIER_SLUG } from '@/types/Closet';
import { useEllaAnalytics } from '@/components/hook/analytics';

export const PRODUCT_CARD_IMG_HEIGHT = 44;
export const PRODUCT_CARD_IMG_WIDTH = 44;

type Sizes = {
  id: string;
  label?: string;
};

export enum ELLA_RETAILER {
  VIVRELLE = 'vivrelle',
  REVOLVE = 'revolve'
}

export type EllaProductData = {
  id?: string;
  name: string;
  title: string;
  price: number;
  retail_price: number;
  sizes?: Sizes[];
  provider: string;
  category: string;
  subcategory: string;
  subsubcategory: string;
  subscriptionLevel?: string;
  score: number;
  link?: string;
  cj_link?: string;
  gpt_context?: string;
  size?: string;
  year?: number;
  store?: string;
  brand?: string;
  mainImage: string;
  item_group_id: string;
  description: string;
  common_color: string;
  product_type: string;
  allSizes?: string[];
  image_link?: string;
  image?: string;
  cartId?: string;
  revolveCartId?: string;
  sku?: string;
  tier?: string;
  taxItemId?: string;
};

export type EllaMobileProductData = {
  id?: string;
  name: string;
  title: string;
  price: string;
  retail_price: string;
  sizes?: Sizes[];
  provider: string;
  category: string;
  subcategory: string;
  subsubcategory: string;
  score: number;
  link?: string;
  cj_link?: string;
  gpt_context?: string;
  size?: string;
  year?: number;
  store?: string;
  brand?: string;
  mainImage: string;
  item_group_id: string;
  description: string;
  common_color: string;
  product_type: string;
  allSizes?: string[];
  image?: string;
  cartId?: string;
  sku?: string;
  updated_title: string;
  current_price: string;
  sub_category: string;
  sub_sub_category: string;
  image_link: string;
};

export type EllaCartProductProps = {
  canRemoveItem?: boolean;
  handleRemoveItem?: () => void;
  product: EllaProductData;
  className?: string;
  isRemoving?: boolean;
  isPlacingOrder?: boolean;
  onUpdateItem?: (id: string, size: string) => void;
};

function EllaCartProduct(props: EllaCartProductProps) {
  const {
    canRemoveItem,
    className = '',
    handleRemoveItem,
    product,
    isRemoving,
    isPlacingOrder,
    onUpdateItem
  } = props;

  const [updateError, setUpdateError] = useState<string | null>(null);

  const { metadata } = useEllaAnalytics();

  const trackProductEvent = (eventName: string, additionalData?: any) => {
    analytics.track(eventName, {
      ...metadata,
      ...additionalData
    });
  };

  const updateCartItemSize = async (newSize: string) => {
    if (!product.id || !product.revolveCartId) return;

    setUpdateError(null);

    try {
      onUpdateItem?.(product.id, newSize);
      await updateCartItem(product.revolveCartId, {
        id: product.id,
        size: newSize,
        product: { ...product, size: newSize }
      });
    } catch (error) {
      console.error('Error updating item size:', error);
      setUpdateError(
        error instanceof Error
          ? error.message
          : 'Failed to update size. Please try again.'
      );
    }
  };

  const handleSizingSelect = async (
    event: ChangeEvent<HTMLSelectElement | HTMLInputElement>
  ) => {
    trackProductEvent('Ella AI Checkout Size - Updated', {
      item_origin: product.provider,
      item_group_id: product.item_group_id,
      common_color: product.common_color,
      brand: product.brand,
      updated_title: product.name,
      retail_price: product.retail_price,
      current_price: product.price,
      product_type: product.product_type,
      category: product.category,
      sub_category: product.subcategory,
      sub_sub_category: product.subsubcategory,
      link: product.link,
      year: product.year,
      store: product.provider,
      size: product.size,
      gpt_context: product.gpt_context,
      updated_size: event.currentTarget.value
    });
    const { value } = event.currentTarget;
    if (value !== product.size) {
      await updateCartItemSize(value);
    }
  };

  const handleVivrelleProductLinkClick = () => {
    trackProductEvent('Ella AI Cart Item Vivrelle Link - Clicked', {
      parentSku: product.sku || product.item_group_id,
      parentSkuTier: product.tier
    });
  };

  const handleRevolveProductLinkClick = () => {
    trackProductEvent('Ella AI Checkout Revolve/FWRD Item - Clicked', {
      itemOrigin: product.provider,
      item_group_id: product.item_group_id,
      common_color: product.common_color,
      product_type: product.product_type,
      updated_title: product.name,
      retail_price: product.retail_price,
      current_price: product.price,
      category: product.category,
      sub_category: product.subcategory,
      sub_sub_category: product.subsubcategory,
      link: product.link,
      size: product.size,
      year: product.year,
      store: product.provider,
      gpt_context: product.gpt_context
    });
  };

  const handleRemoveItemClick = () => {
    trackProductEvent('Ella AI Checkout Remove Item - Clicked', {
      itemOrigin: product.provider,
      item_group_id: product.item_group_id,
      common_color: product.common_color,
      brand: product.brand,
      product_type: product.product_type,
      updated_title: product.name,
      retail_price: product.retail_price,
      current_price: product.price,
      category: product.category,
      sub_category: product.subcategory,
      sub_sub_category: product.subsubcategory,
      link: product.link,
      size: product.size,
      year: product.year,
      store: product.provider,
      gpt_context: product.gpt_context
    });
    if (handleRemoveItem) {
      handleRemoveItem();
    }
  };

  return (
    <div className={cn('EllaCartProduct', className)}>
      <div className='EllaCartProduct-details'>
        <Image
          className='EllaCartProduct-image'
          src={product.image || FallbackImage}
          height={PRODUCT_CARD_IMG_HEIGHT}
          width={PRODUCT_CARD_IMG_WIDTH}
          alt={product.name}
        />
        <div className='EllaCartProduct-container-name-size'>
          {product.link ? (
            product.link.startsWith('http') ? (
              <a
                href={product.cj_link || product.link}
                target='_blank'
                rel='noopener noreferrer'
                className='EllaCartProduct-link'
                onClick={
                  product.provider !== ELLA_RETAILER.VIVRELLE
                    ? handleRevolveProductLinkClick
                    : handleVivrelleProductLinkClick
                }
              >
                {product.name}
              </a>
            ) : (
              <Link
                href={product.link}
                newTab={true}
                underline
                variant='link-xs'
                onClick={
                  product.provider !== ELLA_RETAILER.VIVRELLE
                    ? handleRevolveProductLinkClick
                    : handleVivrelleProductLinkClick
                }
              >
                {product.name}
              </Link>
            )
          ) : (
            <span className='EllaCartProduct-name'>{product.name}</span>
          )}

          {updateError && (
            <Text
              variant='text-s'
              className='EllaCartProduct-error'
            >
              {updateError}
            </Text>
          )}
          {product.allSizes &&
          product.allSizes.length >= 2 &&
          !isPlacingOrder ? (
            <div
              className='EllaCartProduct-select-wrapper'
              style={{ maxWidth: '100px' }}
            >
              <Select
                className={cn('EllaCartProduct-sizing-select')}
                value={product.size}
                onChange={handleSizingSelect}
              >
                {product.allSizes.map((size: string, index: number) => (
                  <option
                    key={index}
                    value={size}
                  >
                    {size}
                  </option>
                ))}
              </Select>
            </div>
          ) : product.size && product.size !== 'all' ? (
            <Text variant='text-s'>({product.size})</Text>
          ) : null}
        </div>
      </div>

      <div className='EllaCartProduct-details'>
        <Text variant='text-s'>
          {product.provider === ELLA_RETAILER.VIVRELLE
            ? 'Included'
            : USDFormatter.format(product.price ? product.price / 100 : 0)}
        </Text>

        {canRemoveItem && (
          <div className='EllaCartProduct-removeItem'>
            {isRemoving ? (
              <LoadingSpinner size={16} />
            ) : !isPlacingOrder ? (
              <Button
                variant='icon'
                onClick={handleRemoveItemClick}
              >
                <Image
                  alt=''
                  src={CloseIcon}
                  height={20}
                  width={20}
                />
              </Button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

export default EllaCartProduct;
